import { AccountInterface, GetTransactionReceiptResponse, RpcProvider } from 'starknet';

const POLL_INTERVAL = 3000;
const MAX_RETRIES = 40; // 40 retries * 3s = 2 minutes max wait
const RPC_UNHEALTHY_THRESHOLD = 5; // After 5 consecutive failures, consider RPC unhealthy

export type TransactionReceiptError = {
  type: 'timeout' | 'rpc_unhealthy' | 'aborted' | 'unknown';
  message: string;
  retryCount: number;
};

export class TransactionReceiptTimeoutError extends Error {
  public readonly type = 'timeout' as const;
  public readonly retryCount: number;

  constructor(retryCount: number, txHash: string) {
    super(`Transaction receipt not found after ${retryCount} attempts (tx: ${txHash})`);
    this.name = 'TransactionReceiptTimeoutError';
    this.retryCount = retryCount;
  }
}

export class RpcUnhealthyError extends Error {
  public readonly type = 'rpc_unhealthy' as const;
  public readonly retryCount: number;

  constructor(retryCount: number) {
    super(`RPC appears unhealthy after ${retryCount} consecutive failures`);
    this.name = 'RpcUnhealthyError';
    this.retryCount = retryCount;
  }
}

export type WaitForTransactionReceiptOptions = {
  maxRetries?: number;
  pollInterval?: number;
  abortSignal?: AbortSignal;
  onRetry?: (retryCount: number, maxRetries: number) => void;
  onRpcUnhealthy?: () => void;
};

export async function waitForTransactionReceipt(
  tx: string,
  account: AccountInterface,
  provider: RpcProvider,
  options: WaitForTransactionReceiptOptions = {},
): Promise<GetTransactionReceiptResponse> {
  const {
    maxRetries = MAX_RETRIES,
    pollInterval = POLL_INTERVAL,
    abortSignal,
    onRetry,
    onRpcUnhealthy,
  } = options;

  let retryCount = 0;
  let consecutiveRpcFailures = 0;

  while (retryCount < maxRetries) {
    // Check if aborted
    if (abortSignal?.aborted) {
      throw new DOMException('Transaction receipt wait aborted', 'AbortError');
    }

    try {
      const receipt = await provider.getTransactionReceipt(tx);
      // Reset consecutive failures on success
      consecutiveRpcFailures = 0;
      return receipt;
    } catch (error) {
      retryCount++;
      consecutiveRpcFailures++;

      // Check if RPC appears unhealthy
      if (consecutiveRpcFailures >= RPC_UNHEALTHY_THRESHOLD) {
        console.error(`RPC appears unhealthy after ${consecutiveRpcFailures} consecutive failures`);
        onRpcUnhealthy?.();
      }

      console.warn(
        `Failed to find tx with hash (attempt ${retryCount}/${maxRetries})! Trying again in ${pollInterval}ms.`,
      );

      // Notify caller of retry
      onRetry?.(retryCount, maxRetries);

      if (retryCount >= maxRetries) {
        // Check if it's likely an RPC issue vs transaction not existing
        if (consecutiveRpcFailures >= RPC_UNHEALTHY_THRESHOLD) {
          throw new RpcUnhealthyError(retryCount);
        }
        throw new TransactionReceiptTimeoutError(retryCount, tx);
      }
    }

    // Wait before next attempt, but check for abort
    await waitWithAbort(pollInterval, abortSignal);
  }

  throw new TransactionReceiptTimeoutError(retryCount, tx);
}

async function waitWithAbort(ms: number, abortSignal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      resolve();
    }, ms);

    if (abortSignal) {
      if (abortSignal.aborted) {
        clearTimeout(timeoutId);
        reject(new DOMException('Aborted', 'AbortError'));
        return;
      }

      const abortHandler = () => {
        clearTimeout(timeoutId);
        reject(new DOMException('Aborted', 'AbortError'));
      };

      abortSignal.addEventListener('abort', abortHandler, { once: true });

      // Clean up the event listener when the timeout completes
      const originalResolve = resolve;
      resolve = () => {
        abortSignal.removeEventListener('abort', abortHandler);
        originalResolve();
      };
    }
  });
}
